<template>
  <div class="tag-input-wrapper">
    <v-combobox
      v-model="tags"
      :items="allTags"
      multiple
      chips
      closable-chips
      variant="outlined"
      density="compact"
      hide-details
      class="tag-input-combobox"
      :disabled="$store.state.isWorkflowDisabled"
      @update:model-value="onTagsUpdate"
      placeholder="Type to add tags..."
      :menu-props="{
        scrim: false,
        closeOnContentClick: false
      }"
    >
      <template #chip="{ item, props }">
        <v-chip
          v-bind="props"
          size="small"
          class="tag-chip"
        >
          {{ item.value }}
        </v-chip>
      </template>
    </v-combobox>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import workflowApi from '@/api/workflowApi'

export default {
  name: 'TagInput',
  props: {
    cardId: {
      type: String,
      required: true
    },
    modelValue: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:modelValue'],
  data () {
    return {
      tags: []
    }
  },
  computed: {
    ...mapState({
      workflow: state => state.workflow.workflow
    }),
    allTags () {
      // Get all unique tags from all cards in the current workflow
      const allTags = new Set()

      if (this.workflow?.domainEvents) {
        this.workflow.domainEvents.forEach(event => {
          if (event.cards) {
            event.cards.forEach(card => {
              if (card.tags && Array.isArray(card.tags)) {
                card.tags.forEach(tag => allTags.add(tag))
              }
            })
          }
        })
      }

      return Array.from(allTags).sort()
    },
    currentCard () {
      // Find the current card in the workflow
      if (!this.workflow?.domainEvents) return null

      for (const event of this.workflow.domainEvents) {
        if (event.cards) {
          const card = event.cards.find(c => c.id === this.cardId)
          if (card) return card
        }
      }
      return null
    }
  },
  watch: {
    currentCard: {
      immediate: true,
      handler (card) {
        if (card) {
          // Always update tags, even if undefined or empty
          this.tags = Array.isArray(card.tags) ? [...card.tags] : []
        }
      }
    }
  },
  methods: {
    async onTagsUpdate (newTags) {
      // Normalize tags: filter out empty strings and ensure array
      const normalizedTags = Array.isArray(newTags)
        ? newTags
          .filter((tag) => tag && typeof tag === 'string' && tag.trim())
          .map((tag) => tag.trim())
        : []

      this.tags = normalizedTags

      // Save to backend
      try {
        await workflowApi.updateCardsMutation(this.$route.params.id, [{
          cardId: this.cardId,
          tags: normalizedTags,
          version: this.currentCard?.version
        }])
        this.$emit('update:modelValue', normalizedTags)
      } catch (error) {
        console.error('Error saving tags:', error)
        // Revert to previous tags on error
        if (this.currentCard) {
          this.tags = Array.isArray(this.currentCard.tags) ? [...this.currentCard.tags] : []
        }
      }
    }
  },
  mounted () {
    // Load existing tags for this card from workflow state
    if (this.currentCard && this.currentCard.tags) {
      this.tags = this.currentCard.tags || []
      this.$emit('update:modelValue', this.tags)
    }
  }
}
</script>

<style scoped>
.tag-input-wrapper {
  width: 100%;
}

.tag-input-combobox {
  width: 100%;
}

.tag-chip {
  margin: 2px;
}

:deep(.tag-chip .v-chip__close) {
  color: var(--dark-2) !important;
  opacity: 0.7;
  font-size: 16px !important;
  width: 18px !important;
  height: 18px !important;
}

:deep(.tag-chip .v-chip__close:hover) {
  opacity: 1;
  color: var(--dark-3) !important;
}

:deep(.tag-input-combobox.v-combobox) {
  .v-field__input {
    min-height: 20px !important;
    padding-top: 5px !important;
    padding-bottom: 5px !important;
    padding-left: 5px !important;
  }

  .v-input__details {
    display: none !important;
  }

  .v-combobox__selection {
    overflow: hidden;
  }
}
</style>
